import { encode } from "gpt-tokenizer";
import { cloneDeep } from "lodash-es";
import { nanoid } from "nanoid";

import { api, django } from "@/api";
import { Page, Version } from "@/types";
import { proseMirrorJSONToText } from "@/utils";

export const newPage = ({
  projectID,
  address,
  isDefault = false,
  lateralID = nanoid(),
}: {
  projectID: string;
  address: string;
  isDefault: boolean;
  lateralID: string;
}) => {
  return {
    content: "{}",
    created: new Date().toJSON(),
    deleted: false,
    isDefault,
    lateralID,
    originalAuthor: address,
    projectID,
    meta: "{}",
  };
};

const formatPage = (p: { stream_id: string; stream_content: any }): Page => {
  const page: Page = cloneDeep(p.stream_content);
  page.id = p.stream_content.lateralID;
  page.ceramicID = p.stream_id;
  page.created = new Date(page.created);
  // @ts-ignore
  page.content = JSON.parse(page.content ?? "{}");
  // @ts-ignore
  page.meta = JSON.parse(page.meta ?? "{}");
  // @ts-ignore
  delete page.lateralID;
  return page;
};

export const getProjectPages = async ({
  signal,
  queryKey,
}: {
  signal: AbortSignal;
  queryKey: string[];
}): Promise<Page[]> => {
  const projectId = queryKey[1];
  const response = await api.get(
    `kjzl6hvfrbw6cb3f0gw4cjkijrb52ik1qx4th12tseoioud5f8slp57hezer3my`,
    {
      params: {
        select: "stream_id,stream_content",
        "stream_content->>projectID": `eq.${projectId}`,
        "stream_content->>deleted": `neq.true`,
      },
      signal,
    }
  );
  return response.data.map(formatPage);
};
export const getDefaultPageId = async (
  signal: AbortSignal | undefined,
  projectId?: string
): Promise<string> => {
  const response = await api.get(`rpc/get_default_page`, {
    params: { p_project_id: projectId },
    signal,
    headers: { Accept: "application/vnd.pgrst.object+json" },
  });
  return response.data.lateral_id;
};

export const getPage = async (
  signal: AbortSignal | undefined,
  projectId?: string,
  pageId?: string
): Promise<Page> => {
  const response = await api.get(`rpc/get_page`, {
    params: { p_project_id: projectId, p_lateral_id: pageId },
    signal,
    headers: { Accept: "application/vnd.pgrst.object+json" },
  });
  return formatPage(response.data);
};

export const getPageIds = async (
  signal: AbortSignal | undefined,
  projectId?: string
): Promise<string[]> => {
  const response = await api.get("/rpc/get_distinct_lateral_ids", {
    params: {
      p_project_id: projectId,
    },
    signal,
  });
  const lateralIds: string[] = response.data.map((item: { lateral_id: string }) => item.lateral_id);
  const distinctLateralIds = [...new Set(lateralIds)];
  return distinctLateralIds;
};

export const getPageVersions = async (
  signal: AbortSignal | undefined,
  name: string
): Promise<Version[]> => {
  const response = await django.get(`/editor/snapshots/`, {
    params: {
      name,
      ordering: "-created_at",
    },
    signal,
  });
  return response.data;
};

export const getNodePageContents = async (
  signal: AbortSignal | undefined,
  projectId?: string,
  pageIds?: string[]
): Promise<{ [key: string]: { content: string; tokens: number } }> => {
  const response = await api.post(
    `rpc/get_pages_content`,
    { p_project_id: projectId, p_lateral_ids: `{"${pageIds?.join('","')}"}` },
    {
      signal,
    }
  );
  return response.data.reduce(
    (
      map: { [key: string]: { content: string; tokens: number } },
      r: { id: string; content: string }
    ) => {
      let content = "";
      try {
        const parsedJson = JSON.parse(r.content);
        if ("editorState" in parsedJson) {
          content = proseMirrorJSONToText(parsedJson.editorState);
        }
      } catch {}
      map[r.id] = { content, tokens: encode(content).length };
      return map;
    },
    {}
  );
};
